import React, { useEffect, useState, useCallback, useRef } from 'react';
import { localSocket, onIdReady, remoteSocket } from "../services/socket";
import { api_url, remote_url } from "../constants/api";
import { pc_id } from "../services/socket";
import { useNavigate } from "react-router-dom";
import { BrandLogo } from "../branding/BrandingContext";

interface MouseState {
    id: string;
    isPressed: boolean;
    port?: string;
}

const waitForMouseRetry = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const ActivationPage: React.FC = () => {
    const [mice, setMice] = useState<Record<string, MouseState>>({});
    const [miceIds, setMiceIds] = useState<string[]>([]);
    const [connected, setConnected] = useState(localSocket.connected);
    const [activationCode, setActivationCode] = useState("00000");

    const navigate = useNavigate();
    const slotColors = ["#8b7b1e", "#0a6b31", "#4a235a", "#922b21", "#566573", "#9b1b30"];
    const activationRequestInFlightRef = useRef(false);
    const activationCodeResolvedRef = useRef(false);

    const applyActivationPayload = useCallback((payload: any) => {
        const code = payload?.code ?? payload?.data?.code;
        if (typeof code === "string" && code.trim()) {
            activationCodeResolvedRef.current = true;
            setActivationCode(code.trim());
        }
    }, []);

    const getActivationCode = useCallback(async () => {
        if (activationRequestInFlightRef.current || activationCodeResolvedRef.current) {
            return;
        }

        activationRequestInFlightRef.current = true;
        try {
            const response = await fetch(`${api_url}/getActivationCode`);
            const data = await response.json();
            applyActivationPayload(data);
        } catch (error) {
            console.error(error);
        } finally {
            activationRequestInFlightRef.current = false;
        }
    }, [applyActivationPayload]);

    const plugMouse = async (id: string, port: string, authToken: string) => {
        if (!id || !port || !authToken) return false;
        let delay = 500;
        for (let attempt = 0; attempt < 5; attempt += 1) {
            try {
                const response = await fetch(remote_url + "/api/v1/device/add-mouse", {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": "Bearer " + authToken },
                    body: JSON.stringify({ mouseId: id, portId: port }),
                });
                const result = await response.json();
                if (response.ok && result.success) return true;
                if (response.status >= 400 && response.status < 500 && response.status !== 409 && response.status !== 429) return false;
            } catch (err) {
                console.error("Failed to plug mouse:", id, err);
            }
            await waitForMouseRetry(delay);
            delay = Math.min(delay * 2, 8000);
        }
        return false;
    };

    useEffect(() => {
        const unsubscribeIdReady = onIdReady(async (readyPcId) => {
            try {
                const request = await fetch(`${remote_url}/api/v1/device/login-device`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ deviceId: readyPcId })
                });

                const data = await request.json();
                if (request.ok && data.success) {
                    localStorage.setItem('token', data.token);
                    if (data.stationId) localStorage.setItem('stationId', data.stationId);
                    navigate("/select-game");
                }
            } catch (err) {
                console.error(err);
            }
        });

        return () => {
            unsubscribeIdReady();
        };
    }, [navigate]);

    useEffect(() => {
        const handleConnect = () => {
            setConnected(true);
            if (!activationCodeResolvedRef.current) {
                void getActivationCode();
            }
            localSocket.emit("requestStatus", "sync");
        };

        if (localSocket.connected) handleConnect();

        localSocket.on('connect', handleConnect);
        localSocket.on('activation_code', applyActivationPayload);
        return () => {
            localSocket.off('connect', handleConnect);
            localSocket.off('activation_code', applyActivationPayload);
        };
    }, [applyActivationPayload, getActivationCode]);

    useEffect(() => {
        const onStatus = (data: { event: string; id: string; port?: string; portId?: string }) => {
            if (!data.id) return;

            if (data.event === 'connected') {
                setMice(prev => ({ ...prev, [data.id]: { id: data.id, isPressed: false, port: data.port || data.portId || "" } }));
                setMiceIds(prev => prev.includes(data.id) ? prev : [...prev, data.id]);
            } else if (data.event === 'disconnected') {
                setMice(prev => {
                    const next = { ...prev };
                    delete next[data.id];
                    return next;
                });
                setMiceIds(prev => prev.filter(mid => mid !== data.id));
            }
        };

        const onClick = (data: { id: string }) => {
            if (!data.id) return;
            setMice(prev => (prev[data.id] ? { ...prev, [data.id]: { ...prev[data.id], isPressed: true } } : prev));
            setTimeout(() => {
                setMice(prev => (prev[data.id] ? { ...prev, [data.id]: { ...prev[data.id], isPressed: false } } : prev));
            }, 100);
        };

        const onSync = (data: string[]) => {
            if (!Array.isArray(data)) return;
            const validIds = data.filter(id => !!id);
            const syncMap: Record<string, MouseState> = {};
            validIds.forEach(id => syncMap[id] = { id, isPressed: false });
            setMice(prev => {
                validIds.forEach(id => {
                    syncMap[id] = { ...syncMap[id], port: prev[id]?.port || "" };
                });
                return syncMap;
            });
            setMiceIds(validIds);
        };

        const onDisconnect = () => {
            setConnected(false);
            setMice({});
            setMiceIds([]);
        };

        const handleActivation = async (data: any) => {
            if (!data.stationId) return;
            localStorage.setItem('stationName', data.stationName);
            localStorage.setItem('stationId', data.stationId);

            try {
                const loginReq = await fetch(`${remote_url}/api/v1/device/login-device`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ deviceId: pc_id })
                });

                const loginData = await loginReq.json();
                if (loginReq.ok && loginData.success) {
                    localStorage.setItem('token', loginData.token);

                    for (const id of miceIds) {
                        const port = mice[id]?.port || "";
                        if (port) await plugMouse(id, port, loginData.token);
                    }

                    navigate('/select-game');
                }
            } catch (error) {
                console.error(error);
            }
        };

        localSocket.on('status', onStatus);
        localSocket.on('click', onClick);
        localSocket.on('sync', onSync);
        localSocket.on('disconnect', onDisconnect);
        remoteSocket.on('deviceActivated', handleActivation);

        return () => {
            localSocket.off('status', onStatus);
            localSocket.off('click', onClick);
            localSocket.off('sync', onSync);
            localSocket.off('disconnect', onDisconnect);
            remoteSocket.off('deviceActivated', handleActivation);
        };
    }, [navigate, miceIds, mice]);

    useEffect(() => {
        if (localSocket.connected) localSocket.emit("requestStatus", "sync");
    }, []);

    return (
        <div className="h-screen bg-[#071626]/90 text-slate-200 flex flex-col overflow-hidden" style={{ fontFamily: "'Born Strong', sans-serif" }}>
            <style dangerouslySetInnerHTML={{ __html: `
             .hide-scrollbar::-webkit-scrollbar { display: none; }
             .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}} />

            <div className="h-16 border-b border-white/5 bg-black/40 flex items-center justify-between px-8 shrink-0 z-10">
                <div className="flex items-center gap-4">
                    <BrandLogo className="h-8 max-w-64 text-2xl" />
                </div>
                <div className="flex items-center gap-2 px-4 py-1.5 bg-black/40 border border-white/5 rounded-full">
                    <div className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-500 shadow-[0_0_8px_#10b981]' : 'bg-red-600 animate-pulse'}`} />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {connected ? 'Socket Connected' : 'Attempting Connection'}
                    </span>
                </div>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center relative p-6">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#0b1e33_0%,_#071626_100%)]" />
                <div className="z-10 bg-black/30 border border-white/10 p-12 rounded-[3rem] shadow-[0_35px_60px_-15px_rgba(0,0,0,0.6)] text-center max-w-lg w-full">
                    <span className="text-[11px] font-black text-red-600 uppercase tracking-[0.5em] block mb-2">Device Activation Required</span>
                    <div className="bg-black/60 rounded-[2rem] py-10 border border-white/5 shadow-[inset_0_2px_10px_rgba(0,0,0,0.8)]">
                        <div className="text-8xl font-mono font-black text-white tracking-[0.15em]">{activationCode}</div>
                    </div>
                    <div className="mt-10 pt-8 border-t border-white/5 font-mono text-[12px] text-white/80">{pc_id}</div>
                </div>
            </div>

            <div className="h-60 w-full bg-black/50 border-t border-white/10 px-8 py-6 flex flex-col shadow-[0_-10px_30px_rgba(0,0,0,0.3)]">
                <div className="flex items-center justify-between mb-5">
                    <span className="text-sm font-black text-white uppercase tracking-widest italic">Hardware Monitor</span>
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{miceIds.length} Slots Active</span>
                </div>
                <div className="flex-1 flex gap-5 overflow-x-auto pb-4 hide-scrollbar">
                    {miceIds.map((id, index) => {
                        const mouse = mice[id];
                        const color = slotColors[index % slotColors.length];
                        return (
                            <div key={id} className={`w-64 shrink-0 flex flex-col rounded-2xl border-2 transition-all duration-100 ${mouse?.isPressed ? 'border-white bg-white/10 scale-95' : 'border-white/5 bg-black/20'}`}>
                                <div className="h-32 flex items-center px-4 border-b border-white/5">
                                    <div style={{ backgroundColor: color }} className="w-8 h-8 rounded-lg flex items-center justify-center text-lg font-black text-black/80 mr-3">{index + 1}</div>
                                    <div className="text-[10px] font-mono font-bold text-slate-200 truncate">{id}</div>
                                </div>
                                <div className="flex-1 flex items-center justify-center p-4">
                                    <div className={`text-[10px] font-black uppercase ${mouse?.isPressed ? 'text-white animate-pulse' : 'text-slate-600'}`}>
                                        {mouse?.isPressed ? 'Receiving' : 'Standby'}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ActivationPage;
